import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from '../lib/api.js';

export type TeamUser = {
  id: string;
  full_name: string;
  email: string;
  role: 'super_admin' | 'admin' | 'consultant';
  created_at: string;
  deactivated_at: string | null;
  last_active_at: string | null;
};

export type PendingInvitation = {
  id: string;
  email: string;
  role: 'admin' | 'consultant';
  invited_by_name: string | null;
  expires_at: string;
  created_at: string;
};

export type CreateInvitationPayload = {
  email: string;
  role: 'admin' | 'consultant';
};

export function useTeamUsers() {
  return useQuery({
    queryKey: ['admin', 'team', 'users'],
    queryFn: () => apiGet<{ users: TeamUser[] }>('/admin/team/users'),
    staleTime: 30_000,
  });
}

export function usePendingInvitations() {
  return useQuery({
    queryKey: ['admin', 'team', 'invitations'],
    queryFn: () => apiGet<{ invitations: PendingInvitation[] }>('/admin/team/invitations'),
    staleTime: 30_000,
  });
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvitationPayload) =>
      apiPost<{ id: string; email: string; role: string; expires_at: string }>(
        '/admin/team/invitations',
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'team', 'invitations'] });
      qc.invalidateQueries({ queryKey: ['admin', 'team', 'users'] });
    },
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiPost<{ id: string; deactivated_at: string }>(`/admin/team/users/${userId}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team', 'users'] }),
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiPost<{ id: string; deactivated_at: null }>(`/admin/team/users/${userId}/reactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team', 'users'] }),
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ id: string; email: string; role: string; expires_at: string }>(
        `/admin/team/invitations/${id}/resend`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team', 'invitations'] }),
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/admin/team/invitations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team', 'invitations'] }),
  });
}
